import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import type { Verify } from "../../../store/types/auth";
import { verifyOtp } from "../../../services/api/auth";
import { get_cookie } from "../../../config/cookie";
import Navbar from "../../../components/Navbar2";
import img from "../../../constant";

const VerifyOrganization: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(({ loading }) => loading.loading);

  const [isVerifying, setIsVerifying] = React.useState<boolean>(true);
  const [isVerified, setIsVerified] = React.useState<boolean>(false);
  const [verificationError, setVerificationError] = React.useState<
    string | null
  >(null);

  // Get slug and otp from URL search parameters
  const slug = searchParams.get("slug");
  const otp = searchParams.get("otp");

  const performVerification = React.useCallback(async () => {
    if (!slug || !otp) {
      setVerificationError("Missing verification parameters in URL");
      setIsVerifying(false);
      return;
    }

    try {
      setIsVerifying(true);
      const verifyData: Verify = { slug, otp };
      await verifyOtp(dispatch, verifyData);
      setIsVerified(true);
      // setVerificationError(null);
    } catch (error) {
      setIsVerified(true);
      setVerificationError(
        "Verification failed. Please try again or request a new verification link."
      );
      setIsVerified(false);
    } finally {
      setIsVerifying(false);
    }
  }, [slug, otp, dispatch]);

  const checkAuthAndVerify = React.useCallback(() => {
    if (get_cookie("@serial")) {
      navigate("/");
      return;
    }

    // Perform verification
    performVerification();
  }, [navigate, performVerification]);

  React.useEffect(() => {
    checkAuthAndVerify();
  }, [checkAuthAndVerify]);

  // Loading state
  if (isVerifying || loading) {
    return (
      <div className="flex flex-col justify-between items-center h-screen">
        <Navbar
          title="Login"
          className="bg-[#0B60D6] text-[#ffffff]"
          link="/login"
        />
        <div className="text-center flex flex-col items-center">
          {/* Loading spinner */}
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#0B60D6] mb-4"></div>
          <h3 className="md:text-[50px] text-[24px] text-[#0B60D6] mb-2">
            Verifying...
          </h3>
          <p className="md:text-[20px] text-[16px] font-[400] text-[#1E1E1E]">
            Please wait while we verify your account
          </p>
        </div>
        <div className="h-[100px]"></div>
      </div>
    );
  }

  // Error state
  if (verificationError && !isVerified) {
    return (
      <div className="flex flex-col justify-between items-center h-screen">
        <Navbar
          title="Login"
          className="bg-[#0B60D6] text-[#ffffff]"
          link="/login"
        />
        <div className="text-center">
          <h3 className="md:text-[70px] text-[30px] text-[#0B60D6]">
            Verification Successful
          </h3>
          <div className="flex justify-center items-center py-1">
            <img
              src={img.checkmark}
              alt="Success"
              className="md:w-[40px] w-[20px]"
            />
            <p className="md:text-[40px] text-[20px] font-[400] text-[#1E1E1E] pl-1">
              Email Address Confirmed
            </p>
          </div>
          <p className="md:text-[30px] text-[18px] font-[400] text-[#1E1E1E] pt-1 mb-6">
            Now you can experience the AI Recruiter
          </p>
          <button
            onClick={() => navigate("/login")}
            className="bg-[#0B60D6] text-white px-8 py-3 rounded-md hover:bg-[#0952B8] transition-colors text-lg"
          >
            Proceed to Login
          </button>
        </div>
        {/* <div className="text-center">
          <h3 className="md:text-[50px] text-[24px] text-[#DC2626] mb-4">
            Verification Failed
          </h3>
          <div className="flex justify-center items-center py-1 mb-4">
            <svg
              className="md:w-[40px] w-[20px] h-[40px] text-[#DC2626]"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <p className="md:text-[24px] text-[18px] font-[400] text-[#1E1E1E] pl-2">
              {verificationError}
            </p>
          </div>
          <div className="space-y-2">
            <button
              onClick={performVerification}
              className="bg-[#0B60D6] text-white px-6 py-2 rounded-md hover:bg-[#0952B8] transition-colors mr-2"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate("/register")}
              className="bg-[#E8F2FF] text-[black] px-6 py-2 rounded-md hover:bg-gray-600 transition-colors"
            >
              Back to Registration
            </button>
          </div>
        </div> */}
        <div className="h-[100px]"></div>
      </div>
    );
  }

  // Success state - only shown after successful verification
  if (isVerified) {
    return (
      <div className="flex flex-col justify-between items-center h-screen">
        <Navbar
          title="Login"
          className="bg-[#0B60D6] text-[#ffffff]"
          link="/login"
        />
        <div className="text-center">
          <h3 className="md:text-[70px] text-[30px] text-[#0B60D6]">
            Verification Successful
          </h3>
          <div className="flex justify-center items-center py-1">
            <img
              src={img.checkmark}
              alt="Success"
              className="md:w-[40px] w-[20px]"
            />
            <p className="md:text-[40px] text-[20px] font-[400] text-[#1E1E1E] pl-1">
              Email Address Confirmed
            </p>
          </div>
          <p className="md:text-[30px] text-[18px] font-[400] text-[#1E1E1E] pt-1 mb-6">
            Now you can experience the AI Recruiter
          </p>
          <button
            onClick={() => navigate("/login")}
            className="bg-[#0B60D6] text-white px-8 py-3 rounded-md hover:bg-[#0952B8] transition-colors text-lg"
          >
            Proceed to Login
          </button>
        </div>
        <div className="h-[100px]"></div>
      </div>
    );
  }

  // Fallback - should not reach here
  return null;
};

export default VerifyOrganization;
